import { Phone, Mail, MessageCircle, Globe, MapPin, Send, Loader2 } from "lucide-react";
import { BRAND, WA_LINK } from "../lib/constants";
import { PageHero } from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { useState } from "react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone & message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message received! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact TASSEI"
        title="Let's talk about your career."
        desc="Whether it's a course query, university admission, or partnership — drop a message or reach us directly on WhatsApp."
        image="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="Team collaboration"
      />

      <section className="pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          {/* Info cards */}
          <div className="lg:col-span-5 space-y-4">
            <a
              href={WA_LINK("Hi TASSEI, I want a free consultation")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="block bg-[#00B37E] text-white rounded-2xl p-6 hover:shadow-2xl hover:shadow-[#00B37E]/30 transition-all hover:-translate-y-1"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-white/80">WhatsApp (Fastest)</div>
                  <div className="font-heading text-lg font-bold mt-1">{BRAND.phone}</div>
                </div>
              </div>
            </a>

            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid="contact-phone-card"
              className="block bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#00B37E] hover:-translate-y-1 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0A192F] flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#00B37E]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#64748B]">Call us</div>
                  <div className="font-heading text-lg font-bold mt-1 text-[#0A192F]">{BRAND.phone}</div>
                </div>
              </div>
            </a>

            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email-card"
              className="block bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#00B37E] hover:-translate-y-1 transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0A192F] flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#00B37E]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#64748B]">Email</div>
                  <div className="font-heading text-lg font-bold mt-1 text-[#0A192F]">{BRAND.email}</div>
                </div>
              </div>
            </a>

            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0A192F] flex items-center justify-center">
                  <Globe className="w-6 h-6 text-[#00B37E]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#64748B]">Website</div>
                  <div className="font-heading text-lg font-bold mt-1 text-[#0A192F]">{BRAND.website}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-100 text-sm text-[#475569]">
                <MapPin className="w-4 h-4 text-[#00B37E] flex-shrink-0" />
                <span>Operating in Delhi · Expanding in India Soon</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9"
            >
              <h3 className="font-heading text-2xl font-bold text-[#0A192F]">Send us a message</h3>
              <p className="text-sm text-[#64748B] mt-1">We typically reply within a few hours.</p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Your name" required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." required className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="Course enquiry, university admission, etc." className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="How can we help?" rows={5} required className="mt-1.5" />
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button type="submit" disabled={loading} data-testid="contact-submit" className="bg-[#0A192F] hover:bg-[#0F2648] text-white font-semibold rounded-full h-12 px-7">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {loading ? "Sending..." : "Send Message"}
                </Button>
                <a
                  href={WA_LINK("Hi TASSEI, I want a free consultation")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-cta"
                >
                  <Button type="button" variant="outline" className="border-2 border-[#00B37E] text-[#00B37E] hover:bg-[#00B37E] hover:text-white font-semibold rounded-full h-12 px-7 w-full sm:w-auto">
                    <MessageCircle className="w-4 h-4 mr-2" /> Or WhatsApp Us
                  </Button>
                </a>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
